import { RSAA } from 'redux-api-middleware'
import webStorage from '../webStorage'

/**
 * if the action is an RSAA action (for API requests), this
 * middleware will make sure that all the right HTTP headers
 * are being included in all requests to the backend.
 *
 * setting some headers to IGNORE will tell this middleware
 * to completely remove them from the request. This is useful
 * for file uploads where headers for files are best left to
 * the browser (i.e. content boundary info).
 */
export default () => next => action => {
  if (!action[RSAA]) return next(action)
  const headers = action[RSAA].headers || {}

  // we always accept JSON as a response.
  //headers['Accept'] = 'application/json'

  // in most cases, we'll want the token in our request
  // but for login we manually set this header anyway
  // so only add if not already set and not explicitly ignored.
  if (headers['token'] === 'IGNORE') {
    delete headers['token']
  } else if (!headers['Authorization']) {
    // to support SSO, we might be supplying a short-lived token
    // directly to the API and so we should include that. Otherwise,
    // as in 99.9% of the time, we'll need the token from localstorage.
    headers['token'] = headers['token'] || webStorage.getItem('token')
  }

  // be sure that application/json is in the headers
  // unless we specifically are saying to ignore headers.
  // This is for media uploads where browser sets headers automatically.
  if (headers['Content-Type'] === 'IGNORE') {
    delete headers['Content-Type']
  } else if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json; charset=utf-8'
  }

  // we need to pass the backend the users timezone
  // so that they can calculate dates from UTC that
  // we send them, as well as dates they send us.
  //
  // currently supporting and expecting:
  // AKST, HST, PST, MST, CST, EST, AST (standard)
  // AKDT, HST, PDT, MDT, CDT, EDT, ADT (daylight savings)
  //
  // outside of these, it's "GMT-0:00" format
  if (headers['tz'] === 'IGNORE') {
    delete headers['tz']
  } else {
    headers['tz'] = new Date()
      .toLocaleTimeString('en-us', { timeZoneName: 'short' })
      .split(' ')[2]
  }

  // it turns out that browser fingerprints aren't as accurate as
  // we need them to be unless we pay for the PRO version. At the
  // request of Krishna, we're going to pass the Google ClientID
  // as the fingerprint. Also, for the strange way we do this, see:
  // https://developers.google.com/analytics/devguides/collection/analyticsjs/ga-object-methods-reference

  // we're going to send the browser fingerprint with every
  // api.sidecarhealth.com request. We don't send it to every
  // 3rd party api because LOB, for example, will throw a CORS
  // error due to sending an unaccepted header.
  if (
    /api\.sidecarhealth\.com/.test(action[RSAA].endpoint) ||
    // member-app's login route, which forwards it to the auth service
    action[RSAA].endpoint === '/api/auth/login' ||
    // adding the second condition to make sure we catch our endpoints
    // that don't automatically return getApiPath and instead use an
    // anonymous or named function to do more business logic before
    // eventually returning getApiPath, these are mostly pagination or
    // heavy data transformation endpoints (i.e. care, doctors, etc...)

    // careful, if we happen to return a 3rd party api endpoint in an
    // anonymous or named function it might throw CORS errors as it
    // isn't expecting a fingerprint header in the request
    typeof action[RSAA].endpoint === 'function'
  ) {
    // if in places like app where we set the fingerprint in a middleware
    // before this appendApiHeaders middleware, if the fingerprint is
    // already there let's just go ahead and use that one
    if (webStorage.getItem('fp')) {
      headers['fingerprint'] = webStorage.getItem('fp')
    }

    // if there's not already a fingerprint in storage and Google Analytics
    // hasn't loaded we don't need/want to send the fingerprint because
    // the getAll() will throw and error and there's nothing to send anyway
    if (!webStorage.getItem('fp') && !!window.ga) {
      window.ga(() => {
        headers['fingerprint'] = window.ga.getAll()[0].get('clientId')
      })
    }
  }

  action[RSAA].headers = headers
  return next(action)
}
